import { Button, ButtonProps, CircularProgress, styled } from '@mui/material';
import { LINE_HEIGHT } from '@/typography/Typography';

const buttonBorderRadius = '5px';

const outlinedSecondaryButtonStyles: React.CSSProperties = {
    padding: '0px 12px',
    margin: '2px',
    height: '24px',
    borderRadius: buttonBorderRadius,
    textTransform: 'none',
    lineHeight: LINE_HEIGHT.SMALL,
    border: '1px solid #D0D5DD',
    color: '#344054',
    fontWeight: 600,
    boxShadow: '0px 1px 2px 0px #1018280D'
};

const secondaryButtonRegularStyles: React.CSSProperties = {
    ...outlinedSecondaryButtonStyles,
    height: '38px',
    padding: '10px 16px 10px 16px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 1px 2px 0px #1018280d',
    border: '1px solid #D0D5DD',
    borderRadius: '8px'
};

export const OutlinedSecondaryButton = (props: ButtonProps) => {
    return (
        <Button
            style={{ ...props.style, ...outlinedSecondaryButtonStyles }}
            {...props}
            sx={{
                border: '1px solid #D0D5DD'
            }}
            variant="outlined"
        >
            {props.children}
        </Button>
    );
};

export const OutlinedSecondaryButtonLG = styled(Button)`
    width: Fill (170px) px;
    height: 44px;
    padding: 10px 18px 10px 18px;
    gap: 8px;
    border-radius: 8px;
    border: 1px 0px 0px 0px;
    opacity: 0px;
    border: 1px solid #d0d5dd;
`;

export const SecondaryButtonGrayLG = styled(Button)`
    border: 1px solid #d0d5dd;
    box-shadow: 0px 1px 2px 0px #1018280d;

    width: Fill (420px) px;
    height: 44;
    border-radius: 8px;
    gap: 8px;
    padding-top: 10px;
    padding-right: 18px;
    padding-bottom: 10px;
    padding-left: 18px;
    border-width: 1px;

    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0%;
    color: #344054;
    text-transform: capitalize;
`;

export const PrimaryButtonLG = styled(Button)`
    width: Fill (170px) px;
    height: 44px;
    gap: 0px;
    border-radius: 8px;
    opacity: 0px;
    background: #1570ef;
    box-shadow: 0px 1px 2px 0px #1018280d;
    text-transform: none;

    &:hover {
        background: #0f5fde;
    }

    & p {
        color: #ffffff;
    }

    &:disabled {
        background: #b0c4de;
        color: #ffffff;
    }

    /* &.Mui-loading {
        color: transparent !important;
    } */
`;

export const SuccessButtonLG = styled(Button)`
    width: Fill (170px) px;
    height: Hug (44px) px;
    gap: 0px;
    border-radius: 8px;
    opacity: 0px;
    background: #17b76a;
    box-shadow: 0px 1px 2px 0px #1018280d;

    &:hover {
        background: #17b76a;
    }

    & p {
        color: #ffffff;
    }

    &:disabled {
        background: #b0c4de;
        color: #ffffff;
    }

    &.Mui-disabled {
        background: #17b76a50;
    }
`;

export const DangerButtonLG = styled(PrimaryButtonLG)`
    background: #d92d20;
    &:hover {
        background: #d92d20;
    }
    & p {
        color: #ffffff;
    }
`;

export const OutlinedSecondaryRegular = (props: ButtonProps) => {
    return (
        <Button
            style={outlinedSecondaryButtonStyles}
            {...props}
            variant="outlined"
        >
            {props.children}
        </Button>
    );
};

export const SecondaryButtonRegular = ({ style, ...props }: ButtonProps) => {
    return (
        <Button
            style={{
                ...secondaryButtonRegularStyles,
                height: props?.size == 'small' ? '24px' : '36px',
                borderRadius: props.size == 'small' ? '5px' : '8px',
                ...style
            }}
            {...props}
            variant="outlined"
        >
            {props.children}
        </Button>
    );
};

export const SecondaryButtonSuccess = ({ style, ...props }: ButtonProps) => {
    const secondaryButtonSuccessStyles: React.CSSProperties = {
        padding: '0px 12px',
        margin: '2px',
        height: '24px',
        borderRadius: buttonBorderRadius,
        textTransform: 'none',
        lineHeight: LINE_HEIGHT.SMALL,
        border: '1px solid #089855',
        backgroundColor: '#089855',
        color: '#FFFFFF',
        fontWeight: 600,
        boxShadow: '0px 1px 2px 0px #1018280D'
    };

    return (
        <Button
            style={{ ...secondaryButtonSuccessStyles, ...style }}
            {...props}
            variant="contained"
        >
            {props.children}
        </Button>
    );
};

export const OutlinedPrimaryButton = (props: ButtonProps) => {
    const outlinedPrimaryButtonStyles: React.CSSProperties = {
        borderRadius: props.size == 'small' ? '5px' : '8px',
        textTransform: 'none',
        backgroundColor: '#EFF8FF',
        height: props.size == 'small' ? '24px' : '36px',
        border: '1px solid #1570EF',
        color: '#1570EF',
        fontWeight: 600,
        lineHeight:
            props.size == 'small' ? LINE_HEIGHT.SMALL : LINE_HEIGHT.MEDIUM
    };

    return (
        <Button
            style={outlinedPrimaryButtonStyles}
            {...props}
            variant="outlined"
        >
            {props.children}
        </Button>
    );
};

export const OutlinedDangerButton = (props: ButtonProps) => {
    const outlinedDangerButtonStyles: React.CSSProperties = {
        borderRadius: props.size == 'small' ? '5px' : '8px',
        textTransform: 'none',
        height: props.size == 'small' ? '24px' : '38px',
        padding: props.size == 'small' ? '4px 12px' : '10px 16px',
        border: '1px solid #D92D20',
        color: '#D92D20',
        fontWeight: 600,
        boxShadow: '0px 1px 2px 0px #1018280D',
        lineHeight:
            props.size == 'small' ? LINE_HEIGHT.SMALL : LINE_HEIGHT.MEDIUM
    };

    return (
        <Button
            style={outlinedDangerButtonStyles}
            {...props}
            variant="outlined"
            sx={{
                '&:hover': {
                    backgroundColor: '#fff',
                    borderColor: '#D92D20'
                },
                '&:disabled': {
                    borderColor: '#D92D20',
                    color: '#D92D20',
                    opacity: 0.6
                },
                ...props.sx
            }}
        >
            {props.children}
        </Button>
    );
};

export const PrimaryButton = ({ style, ...props }: ButtonProps) => {
    const primaryButtonStyles: React.CSSProperties = {
        margin: '2px',
        borderRadius: props.size == 'small' ? '5px' : '8px',
        textTransform: 'none',
        height: props.size == 'small' ? '24px' : '36px',
        padding: '4px 14px 4px 14px',
        fontWeight: 600,
        lineHeight:
            props.size == 'small' ? LINE_HEIGHT.SMALL : LINE_HEIGHT.MEDIUM
    };
    return (
        <Button
            disableElevation
            style={{ ...primaryButtonStyles, ...style }}
            {...props}
            variant="contained"
        >
            {props.children}
        </Button>
    );
};

export const DangerButton = ({ style, ...props }: ButtonProps) => {
    const dangerButtonStyles: React.CSSProperties = {
        margin: '2px',
        borderRadius: props.size == 'small' ? '5px' : '8px',
        textTransform: 'none',
        height: props.size == 'small' ? '24px' : '36px',
        padding: '4px 14px 4px 14px',
        fontWeight: 600,
        backgroundColor: props.disabled ? '#e88d86' : '#D92D20',
        color: '#fff',
        opacity: props.disabled ? 0.4 : 1,
        lineHeight:
            props.size == 'small' ? LINE_HEIGHT.SMALL : LINE_HEIGHT.MEDIUM
    };
    return (
        <Button
            disableElevation
            style={{ ...dangerButtonStyles, ...style }}
            {...props}
            variant="contained"
        >
            {props.children}
        </Button>
    );
};

export const PrimaryInvertedButton = ({
    style,
    loading,
    children,
    ...props
}: ButtonProps & { loading?: boolean }) => {
    const primaryButtonStyles: React.CSSProperties = {
        borderRadius: props.size == 'small' ? '5px' : '8px',
        textTransform: 'none',
        height: props.size == 'small' ? '24px' : '38px',
        fontWeight: 600,
        backgroundColor: '#EFF8FF',
        color: '#1570EF',
        marginLeft: '0px',
        lineHeight:
            props.size == 'small' ? LINE_HEIGHT.SMALL : LINE_HEIGHT.MEDIUM
    };

    return (
        <Button
            disableElevation
            style={{ ...primaryButtonStyles, ...style }}
            {...props}
            disabled={props.disabled || loading}
            variant="contained"
        >
            {loading ? (
                <CircularProgress size={16} color="inherit" />
            ) : (
                children
            )}
        </Button>
    );
};

export const RejectButton = styled(Button)`
    width: 75;
    height: 40;
    gap: 8px;
    border-radius: 8px;
    border-width: 1px;
    padding-top: 10px;
    padding-right: 16px;
    padding-bottom: 10px;
    padding-left: 16px;
    background: #d92d20;
    border: 1px solid #d92d20;
    box-shadow: 0px 1px 2px 0px #1018280d;
    text-transform: none;
    color: #ffffff;

    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;

    &:disabled {
        background: #d0d5dd;
        opacity: 0.4;
        border: 1px solid #d0d5dd;
    }
`;

export const ApproveButton = styled(Button)`
    width: 90;
    height: 40;
    gap: 8px;
    border-radius: 8px;
    border-width: 1px;
    padding-top: 10px;
    padding-right: 16px;
    padding-bottom: 10px;
    padding-left: 16px;
    background: #1570ef;
    border: 1px solid #1570ef;
    box-shadow: 0px 1px 2px 0px #1018280d;
    text-transform: none;
    color: #ffffff;

    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;

    &:disabled {
        background: #d0d5dd;
        opacity: 0.4;
        border: 1px solid #d0d5dd;
    }
`;
